/* Dynamic-comparison engine. Every dimension (surveys / years / sectors / regions / cities,
   plus survey × city splits) reduces to a list of "subjects" with one shape, so the
   CompareView renders all of them with the same cards.

   ctx = { D: Snapshot, lang, t(key,vars), details: Map<id, SurveyDetail> }
   state = { d, i: string[], s: 'all'|surveyId, y: year|null, c: string[] (cities, surveys mode) } */
import { d1, fmt, pctHtml, pctS, deltaChipHtml, surveyName, detailName, period, esc } from './format.js'
import { FOLD, REGIONS, SAT_BUCKETS, SMALL_N, foldedRegionStats, satStats, matrixYears, cityList, regionName, cityName, participantsByYear } from './stats.js'
import { SERIES_COLORS } from '../theme.js'

export const DIMS = ['surveys', 'years', 'sectors', 'regions', 'cities']
export const MAX = { surveys: 4, years: 5, sectors: 3, regions: 4, cities: 4 }
export const COMBO_MAX = 6

export const norm = (s) => String(s || '').toLowerCase().replace(/[ً-ْـ]/g, '').replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي').replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
export const lbById = (D, id) => (D.leaderboard || []).find((r) => r.id === +id)
export const hasLoc = (r) => !!r && (r.located || 0) > 0
export const locPct = (r) => (r && r.participants ? Math.round((r.located || 0) / r.participants * 100) : 0)
export const sectorList = (D) => { const s = []; (D.leaderboard || []).forEach((r) => { if (r.sectorEn && !s.includes(r.sectorEn)) s.push(r.sectorEn) }); return s }
export function sectorName(D, en, lang) {
  const a = (D.sectors || []).find((s) => s.nameEn === en); if (a) return lang === 'ar' ? (a.nameAr || en) : en
  const b = (D.leaderboard || []).find((s) => s.sectorEn === en); return b ? (lang === 'ar' ? (b.sectorAr || en) : en) : en
}
export const yearsOf = (r) => [...new Set((r.yrs || []).map((x) => x[0]))].filter((y) => y >= 2019 && y <= 2031).sort()
const okYear = (D) => { const set = new Set(matrixYears(D)); return (y) => (set.size ? set.has(+y) : true) }

export function defaultItems(D, d) {
  if (d === 'surveys') {
    const base = (r) => norm((r.nameEn || r.nameAr || '').replace(/updated|corrected|final|new|v\d+|\d{4}/gi, ''))
    const out = [];
    (D.leaderboard || []).filter((r) => r.satPct != null).slice().sort((a, b) => b.participants - a.participants)
      .forEach((r) => { if (out.length < 2 && !out.some((o) => base(o) === base(r))) out.push(r) })
    return out.map((r) => String(r.id))
  }
  if (d === 'years') return matrixYears(D).slice(-2).map(String)
  if (d === 'sectors') return sectorList(D).slice(0, 3)
  if (d === 'cities') return cityList(D).slice(0, 3)
  const regs = Object.values(foldedRegionStats(D)).filter((r) => r.rated >= 10000 && r.satPct != null).sort((a, b) => b.satPct - a.satPct)
  const pick = regs.length >= 2 ? [regs[0], regs[regs.length - 1]] : []
  const out = pick.map((r) => String(REGIONS.findIndex((x) => x.ar === r.region))).filter((x) => x !== '-1')
  return out.length >= 2 ? out : ['6', '8']
}
export function freshState(D, d) { return { d, i: defaultItems(D, d), s: 'all', y: null, c: [] } }

/** Parse a query-string-like object into a state (used for shareable URLs). */
export function stateFromQuery(D, q = {}) {
  const d = DIMS.includes(q.d) ? q.d : 'surveys'
  const yv = String(q.y || '')
  const split = (v) => String(v || '').split(',').map((s) => s.trim()).filter(Boolean)
  const st = { d, i: q.i != null ? split(q.i) : defaultItems(D, d), s: q.s || 'all', y: /^\d{4}$/.test(yv) ? +yv : null, c: d === 'surveys' ? split(q.c).slice(0, MAX.cities) : [] }
  st.i = st.i.slice(0, MAX[d])
  return st
}
export function queryFromState(st) {
  const q = { d: st.d, i: st.i.join(',') }
  if (st.d === 'years' || st.d === 'cities') q.s = st.s
  if (st.d !== 'years' && st.y) q.y = String(st.y)
  if (st.d === 'surveys' && st.c.length) q.c = st.c.join(',')
  return q
}
/** Survey ids whose detail files must be loaded before building. */
export function neededIds(D, st) {
  if (st.d === 'surveys') return st.i.map(Number).filter(Boolean)
  if (st.d === 'years' || st.d === 'cities') return st.s === 'all' ? [] : [+st.s]
  if (st.d === 'sectors') return (D.leaderboard || []).filter((r) => st.i.includes(r.sectorEn)).map((r) => r.id)
  return []
}

/* ---------- subjects ---------- */
function finish(s) {
  const B = s.B, r = B.VS + B.S + B.NU + B.D + B.VD
  s.rated = r
  s.satPct = r >= 50 ? d1((B.VS + B.S) / r * 100) : null
  s.score = r >= 50 ? Math.round((5 * B.VS + 4 * B.S + 3 * B.NU + 2 * B.D + B.VD) / r * 10) / 10 : null
  s.pos = r ? d1((B.VS + B.S) / r * 100) : null; s.neu = r ? d1(B.NU / r * 100) : null; s.neg = r ? d1((B.D + B.VD) / r * 100) : null
  s.small = r < SMALL_N
  return s
}
const ySum = (y, yr) => { if (!y) return 0; if (yr) return +y[yr] || 0; let s = 0; for (const k in y) s += +y[k]; return s }
function qMap(sv, yr, lang) {
  const m = new Map();
  (sv.qs || []).forEach((q) => {
    if (!(q.os || []).length) return
    const k = norm(q.tAr || q.tEn); if (!k) return
    let tot = 0; const opts = new Map()
    q.os.forEach((o) => {
      const c = ySum(o.y, yr); tot += c; const ok = norm(o.a || o.e); if (!ok) return
      const cur = opts.get(ok); if (cur) cur.n += c; else opts.set(ok, { t: (lang === 'ar' ? o.a : o.e) || o.a || o.e, n: c, b: o.b })
    })
    if (!tot) return
    const cur = m.get(k)
    if (cur) { cur.tot += tot; opts.forEach((v, ok) => { const c = cur.opts.get(ok); if (c) c.n += v.n; else cur.opts.set(ok, v) }) }
    else m.set(k, { t: (lang === 'ar' ? q.tAr : q.tEn) || q.tAr || q.tEn, tot, opts })
  })
  return m
}
export function subjSurvey(ctx, sv, yr) {
  const B = { VS: 0, S: 0, NU: 0, D: 0, VD: 0 }; let ans = 0;
  (sv.qs || []).forEach((q) => { (q.os || []).forEach((o) => { const c = ySum(o.y, yr); ans += c; if (o.b && B[o.b] != null) B[o.b] += c }); ans += ySum(q.tx, yr) })
  const parts = yr ? (sv.yrs || []).filter((r) => r[0] === yr).reduce((a, r) => a + r[1], 0) : (sv.parts || 0)
  const satYr = {}, partYr = {};
  (sv.satYr || []).forEach(([y, p, n, g]) => { if (y < 2019 || y > 2031) return; const tt = p + n + g; satYr[y] = tt >= 50 ? d1(p / tt * 100) : null });
  (sv.yrs || []).forEach(([y, n]) => { if (y < 2019 || y > 2031) return; partYr[y] = (partYr[y] || 0) + n })
  return finish({ name: detailName(sv, ctx.lang), sub: (ctx.lang === 'ar' ? sv.secAr : sv.secEn) || '', parts, answers: ans, B, questions: sv.questions || (sv.qs || []).length,
    period: period(sv.from, sv.to), satYr, partYr, qmap: qMap(sv, yr, ctx.lang), surveyId: sv.id })
}
export function subjSector(ctx, en, yr) {
  const D = ctx.D, ok = okYear(D)
  const B = { VS: 0, S: 0, NU: 0, D: 0, VD: 0 }; let ans = 0, parts = 0, n = 0; const sy = {}, py = {}; let from = null, to = null;
  (D.leaderboard || []).filter((r) => r.sectorEn === en).forEach((r) => {
    const sv = ctx.details.get(r.id); if (!sv) return
    const s = subjSurvey(ctx, sv, yr)
    for (const k in B) B[k] += s.B[k]; ans += s.answers; parts += s.parts; n++;
    (sv.satYr || []).forEach(([y, p, nu, g]) => { if (y < 2019 || y > 2031) return; (sy[y] = sy[y] || { p: 0, t: 0 }); sy[y].p += p; sy[y].t += p + nu + g });
    (sv.yrs || []).forEach(([y, c]) => { if (y < 2019 || y > 2031 || !ok(y)) return; py[y] = (py[y] || 0) + c })
    if (sv.from && (!from || sv.from < from)) from = sv.from; if (sv.to && (!to || sv.to > to)) to = sv.to
  })
  const satYr = {}; for (const y in sy) if (ok(y)) satYr[y] = sy[y].t >= 50 ? d1(sy[y].p / sy[y].t * 100) : null
  return finish({ name: sectorName(D, en, ctx.lang), sub: '', parts, answers: ans, B, surveys: n, period: from && to ? from + ' → ' + to : '—', satYr, partYr: py, qmap: null })
}
export function subjRegion(ctx, idx, yr) {
  const D = ctx.D; const R = REGIONS[+idx]; if (!R) return null
  const B = { VS: 0, S: 0, NU: 0, D: 0, VD: 0 }; const sy = {};
  (D.regionYearMatrix || []).forEach(([r, y, b, c]) => {
    if (!r || (FOLD[r] || r) !== R.ar || B[b] == null) return
    if (!yr || y === yr) B[b] += c
    if (y >= 2019 && y <= 2031) { (sy[y] = sy[y] || { p: 0, t: 0 }); sy[y].t += c; if (b === 'VS' || b === 'S') sy[y].p += c }
  })
  const satYr = {}; for (const y in sy) satYr[y] = sy[y].t >= SMALL_N ? d1(sy[y].p / sy[y].t * 100) : null
  const fr = foldedRegionStats(D)[R.ar]
  return finish({ name: regionName(R.ar, ctx.lang), sub: '', parts: yr ? null : (fr ? fr.participants : null), answers: null, B, period: '—', satYr, partYr: null, qmap: null })
}
export function subjPlatform(ctx, yr) {
  const D = ctx.D, ok = okYear(D)
  const st = satStats(D, yr); const B = { VS: st.vs, S: st.s, NU: st.nu, D: st.d, VD: st.vd }
  const py = {}; const all = participantsByYear(D); for (const y in all) if (+y >= 2019 && +y <= 2031 && ok(y)) py[y] = all[y]
  const satYr = {}; (D.satisfactionByYear || []).forEach((y) => { if (y.year >= 2019 && y.year <= 2031 && ok(y.year)) satYr[y.year] = y.satPct })
  const ys = Object.keys(py).sort()
  return finish({ name: ctx.t('cmp_all_platform'), sub: '', parts: yr ? (py[yr] || 0) : ((D.participants || {}).total || 0), answers: yr ? null : (D.responses || null), B,
    period: ys.length ? ys[0] + ' → ' + ys[ys.length - 1] : '—', satYr, partYr: py, qmap: null })
}
export function subjSurveyCity(ctx, sv, city, yr) {
  const B = { VS: 0, S: 0, NU: 0, D: 0, VD: 0 }; const sy = {};
  (sv.reg || []).forEach(([r, y, b, c]) => { if (r !== city || B[b] == null) return; if (!yr || y === yr) B[b] += c
    if (y >= 2019 && y <= 2031) { (sy[y] = sy[y] || { p: 0, t: 0 }); sy[y].t += c; if (b === 'VS' || b === 'S') sy[y].p += c } })
  const py = {}; let parts = 0, located = 0;
  (sv.regP || []).forEach(([r, y, c]) => { if (!yr || y === yr) located += c; if (r !== city) return; if (!yr || y === yr) parts += c; if (y >= 2019 && y <= 2031) py[y] = (py[y] || 0) + c })
  const satYr = {}; for (const y in sy) satYr[y] = sy[y].t >= 50 ? d1(sy[y].p / sy[y].t * 100) : null
  const totParts = yr ? (sv.yrs || []).filter((r) => r[0] === yr).reduce((a, r) => a + r[1], 0) : (sv.parts || 0)
  const cov = totParts ? Math.round(located / totParts * 100) : null
  return finish({ name: detailName(sv, ctx.lang) + ' · ' + cityName(city, ctx.lang), sub: cov != null ? ctx.t('cmp_cov', { p: cov }) : '', parts, answers: null, B, questions: null,
    period: '—', satYr, partYr: py, qmap: null, surveyId: sv.id, noReg: !sv.reg, empty: !parts && !located })
}
export function subjPlatformCity(ctx, city, yr) {
  const D = ctx.D, ok = okYear(D)
  const B = { VS: 0, S: 0, NU: 0, D: 0, VD: 0 }; const sy = {};
  (D.regionYearMatrix || []).forEach(([r, y, b, c]) => { if (r !== city || B[b] == null) return; if (!yr || y === yr) B[b] += c
    if (y >= 2019 && y <= 2031 && ok(y)) { (sy[y] = sy[y] || { p: 0, t: 0 }); sy[y].t += c; if (b === 'VS' || b === 'S') sy[y].p += c } })
  const satYr = {}; for (const y in sy) satYr[y] = sy[y].t >= SMALL_N ? d1(sy[y].p / sy[y].t * 100) : null
  const geo = (D.geography || []).find((g) => g.region === city)
  return finish({ name: cityName(city, ctx.lang), sub: '', parts: yr ? null : (geo ? geo.value : null), answers: null, B, period: '—', satYr, partYr: null, qmap: null })
}
export function subjectFull(ctx, st) {
  if (st.s === 'all') return subjPlatform(ctx, null)
  const sv = ctx.details.get(+st.s); return sv ? subjSurvey(ctx, sv, null) : null
}

/** Build the comparison items for a state. */
export function buildItems(ctx, st) {
  const d = st.d, items = []
  if (d === 'surveys') st.i.forEach((id) => { const sv = ctx.details.get(+id); if (!sv) return
    if (st.c.length) st.c.forEach((city) => { const s = subjSurveyCity(ctx, sv, city, st.y); s.key = id + '|' + city; items.push(s) })
    else { const s = subjSurvey(ctx, sv, st.y); s.key = id; items.push(s) } })
  else if (d === 'years') {
    const sv = st.s !== 'all' && ctx.details.get(+st.s)
    st.i.forEach((y) => { y = +y; if (!y) return; const s = sv ? subjSurvey(ctx, sv, y) : (st.s === 'all' ? subjPlatform(ctx, y) : null)
      if (s) { s.name = String(y); s.sub = ''; s.key = String(y); s.surveyId = null; s.year = y; items.push(s) } })
  } else if (d === 'sectors') st.i.forEach((en) => { const s = subjSector(ctx, en, st.y); s.key = en; items.push(s) })
  else if (d === 'cities') {
    const sv = st.s !== 'all' && ctx.details.get(+st.s)
    st.i.forEach((city) => { const s = sv ? subjSurveyCity(ctx, sv, city, st.y) : (st.s === 'all' ? subjPlatformCity(ctx, city, st.y) : null)
      if (s) { if (sv) s.name = cityName(city, ctx.lang); s.key = city; items.push(s) } })
  } else st.i.forEach((ix) => { const s = subjRegion(ctx, ix, st.y); if (s) { s.key = ix; items.push(s) } })
  items.forEach((it, i) => { it.color = SERIES_COLORS[i % SERIES_COLORS.length] })
  return items
}

/** Questions with identical wording in every item (surveys mode / per-survey years mode). */
export function sharedQuestions(items) {
  if (items.length < 2 || items.some((it) => !it.qmap)) return []
  const out = []
  items[0].qmap.forEach((q, k) => {
    if (!items.every((it) => it.qmap.has(k))) return
    const okeys = []; items.forEach((it) => it.qmap.get(k).opts.forEach((v, ok) => { if (!okeys.includes(ok)) okeys.push(ok) }))
    const opts = okeys.map((ok) => {
      const first = items.map((it) => it.qmap.get(k).opts.get(ok)).find(Boolean) || {}
      return { t: first.t || '—', b: first.b, vals: items.map((it) => { const qq = it.qmap.get(k); const o = qq.opts.get(ok); return o && qq.tot ? d1(o.n / qq.tot * 100) : null }) }
    })
    out.push({ t: q.t, opts, tots: items.map((it) => it.qmap.get(k).tot) })
  })
  return out
}

/** Verdict, insights, table rows, and chart series for a built comparison. */
export function analyze(ctx, st, items) {
  const t = ctx.t, lang = ctx.lang, ar = lang === 'ar', isYears = st.d === 'years'
  const nm = (it) => `<b>${esc(it.name)}</b>`
  const elig = items.filter((it) => !it.small && it.satPct != null)
  const ranked = elig.slice().sort((a, b) => b.satPct - a.satPct)
  const top = ranked[0], low = ranked[ranked.length - 1]
  const gap = ranked.length >= 2 ? d1(top.satPct - low.satPct) : null
  const gapC = gap == null ? 'var(--sd-ink-3)' : gap >= 15 ? 'var(--sd-crit)' : gap >= 7 ? 'var(--sd-warn)' : 'var(--sd-good)'
  const subjFull = isYears ? subjectFull(ctx, st) : null
  const scope = isYears ? (subjFull ? esc(subjFull.name) : '') : (st.y ? `${t('yr_tag')} ${st.y}` : t('yr_all'))
  const ins = []; let verdict = ''
  if (isYears) {
    const ey = elig.slice().sort((a, b) => a.year - b.year)
    if (ey.length >= 2) {
      const a = ey[0], b = ey[ey.length - 1], dd = d1(b.satPct - a.satPct), sn = nm(subjFull || { name: '' })
      verdict = ar ? `${dd >= 0 ? 'ارتفع' : 'انخفض'} مؤشر الرضا لـ${sn} من <b>${pctHtml(a.satPct)}</b> في ${a.year} إلى <b>${pctHtml(b.satPct)}</b> في ${b.year} (${deltaChipHtml(dd)} ${t('cmp_pts')}).`
        : `Satisfaction for ${sn} ${dd >= 0 ? 'rose' : 'fell'} from <b>${a.satPct}%</b> in ${a.year} to <b>${b.satPct}%</b> in ${b.year} (${deltaChipHtml(dd)} ${t('cmp_pts')}).`
      ins.push({ s: dd >= 0 ? 'good' : 'crit', ic: dd >= 0 ? '📈' : '📉', html: verdict })
      if (a.parts != null && b.parts != null && a.parts > 0) { const pd = d1((b.parts - a.parts) / a.parts * 100)
        ins.push({ s: pd >= 0 ? 'good' : 'warn', ic: '👥', html: ar ? `تغيّر حجم المشاركة من <b>${fmt(a.parts)}</b> في ${a.year} إلى <b>${fmt(b.parts)}</b> في ${b.year} (${deltaChipHtml(pd, '%')}).` : `Participation moved from <b>${fmt(a.parts)}</b> in ${a.year} to <b>${fmt(b.parts)}</b> in ${b.year} (${deltaChipHtml(pd, '%')}).` }) }
      const best = ranked[0], worst = ranked[ranked.length - 1]
      if (best !== b || worst !== a) ins.push({ s: 'warn', ic: '🗓️', html: ar ? `أفضل سنة ضمن المحدد هي <b>${best.year}</b> (${pctHtml(best.satPct)}) وأدناها <b>${worst.year}</b> (${pctHtml(worst.satPct)}).` : `Best selected year is <b>${best.year}</b> (${best.satPct}%), lowest is <b>${worst.year}</b> (${worst.satPct}%).` })
    }
  } else if (gap != null) {
    verdict = ar ? `يتصدر ${nm(top)} بمؤشر رضا <b>${pctHtml(top.satPct)}</b> مقابل <b>${pctHtml(low.satPct)}</b> لـ${nm(low)} — فارق <b>${gap} ${t('cmp_pts')}</b>${ranked.length > 2 ? ` (${ranked.length} ${t('cmp_items')})` : ''}.`
      : `${nm(top)} leads with <b>${top.satPct}%</b> satisfaction vs <b>${low.satPct}%</b> for ${nm(low)} — a gap of <b>${gap} ${t('cmp_pts')}</b>${ranked.length > 2 ? ` (${ranked.length} ${t('cmp_items')})` : ''}.`
    ins.push({ s: gap >= 15 ? 'crit' : gap >= 7 ? 'warn' : 'good', ic: '🏁', html: verdict })
    const cy = Object.keys(top.satYr || {}).filter((y) => top.satYr[y] != null && low.satYr && low.satYr[y] != null).sort()
    if (cy.length >= 2) { const y0 = cy[0], y1 = cy[cy.length - 1]; const g0 = d1(top.satYr[y0] - low.satYr[y0]), g1 = d1(top.satYr[y1] - low.satYr[y1])
      if (Math.abs(g1 - g0) >= 1) { const narrowing = Math.abs(g1) < Math.abs(g0)
        ins.push({ s: narrowing ? 'good' : 'warn', ic: narrowing ? '↔️' : '↕️', html: ar ? `${narrowing ? 'تقلّص' : 'اتسع'} الفارق بين ${nm(top)} و${nm(low)} من <b>${Math.abs(g0)}</b> ${t('cmp_pts')} في ${y0} إلى <b>${Math.abs(g1)}</b> ${t('cmp_pts')} في ${y1}.` : `The gap between ${nm(top)} and ${nm(low)} ${narrowing ? 'narrowed' : 'widened'} from <b>${Math.abs(g0)}</b> ${t('cmp_pts')} in ${y0} to <b>${Math.abs(g1)}</b> ${t('cmp_pts')} in ${y1}.` }) } }
    const negT = elig.slice().sort((a, b) => b.neg - a.neg)[0]
    if (negT && elig.length > 1) ins.push({ s: 'crit', ic: '⚠️', html: ar ? `أعلى نسبة إجابات سلبية لدى ${nm(negT)} (<b>${pctHtml(negT.neg)}</b>) — يُوصى بمراجعة الأسئلة الأدنى تقييماً.` : `${nm(negT)} carries the highest negative share (<b>${negT.neg}%</b>) — review its lowest-rated questions.` })
    const withP = items.filter((it) => it.parts != null && it.parts > 0).sort((a, b) => b.parts - a.parts)
    if (withP.length >= 2) { const big = withP[0], sm = withP[withP.length - 1]; const k = sm.parts ? Math.round(big.parts / sm.parts * 10) / 10 : null
      ins.push({ s: 'good', ic: '👥', html: ar ? `${nm(big)} الأكبر حجماً بـ <b>${fmt(big.parts)}</b> مشارك${k && k >= 1.5 ? ` — أي نحو <b class="num-ltr">${k}×</b> حجم ${nm(sm)}` : ''}.` : `${nm(big)} is the largest with <b>${fmt(big.parts)}</b> participants${k && k >= 1.5 ? ` — about <b>${k}×</b> the size of ${nm(sm)}` : ''}.` }) }
  }
  const smalls = items.filter((it) => it.small)
  if (smalls.length) ins.push({ s: 'warn', ic: '🔎', html: ar ? `عينة صغيرة (أقل من ${fmt(SMALL_N)} إجابة مقيَّمة) لدى: ${smalls.map(nm).join('، ')} — استُثنيت من الترتيب والخلاصة.` : `Small sample (under ${fmt(SMALL_N)} rated answers) for: ${smalls.map(nm).join(', ')} — excluded from ranking and verdict.` })
  if (!verdict) verdict = t('cmp_noverdict')

  /* table rows */
  const defs = [
    { k: 'cmp_r_sat', v: (it) => it.satPct, f: pctS, hi: true, mark: true, unit: 'pts' },
    { k: 'cmp_r_score', v: (it) => it.score, f: (v) => v.toFixed(1), hi: true, mark: true, unit: 'score' },
    { k: 'cmp_r_pos', v: (it) => it.pos, f: pctS, unit: 'pts' },
    { k: 'cmp_r_neu', v: (it) => it.neu, f: pctS, unit: 'pts' },
    { k: 'cmp_r_neg', v: (it) => it.neg, f: pctS, hi: false, mark: true, unit: 'pts' },
    { k: 'cmp_r_rated', v: (it) => it.rated, f: fmt },
    { k: 'cmp_r_parts', v: (it) => it.parts, f: fmt },
    { k: 'cmp_r_ans', v: (it) => it.answers, f: fmt },
    st.d === 'sectors' ? { k: 'cmp_r_srvs', v: (it) => it.surveys, f: fmt } : (st.d === 'surveys' || (isYears && st.s !== 'all')) ? { k: 'cmp_r_qs', v: (it) => it.questions, f: fmt } : null,
    isYears ? null : { k: 'cmp_r_period', v: (it) => it.period, f: (v) => v, raw: true },
  ].filter(Boolean).filter((r) => items.some((it) => { const v = r.v(it); return v != null && v !== '—' }))
  const rows = defs.map((r) => {
    const vals = items.map((it) => r.v(it))
    let bi = -1, wi = -1
    if (r.mark) { const cand = items.map((it, i) => (!it.small && vals[i] != null ? i : -1)).filter((i) => i >= 0)
      if (cand.length >= 2) { const sorted = cand.slice().sort((a, b) => (r.hi ? vals[b] - vals[a] : vals[a] - vals[b])); bi = sorted[0]; wi = sorted[sorted.length - 1]; if (vals[bi] === vals[wi]) bi = wi = -1 } }
    const nums = vals.filter((v) => typeof v === 'number'); let gapTxt = ''
    if (!r.raw && nums.length >= 2) { const g = Math.max(...nums) - Math.min(...nums); gapTxt = r.unit === 'pts' ? `${d1(g)} ${t('cmp_pts')}` : r.unit === 'score' ? d1(g).toFixed(1) : fmt(g) }
    return { label: t(r.k), cells: vals.map((v, i) => ({ v, text: v == null ? null : r.f(v), raw: !!r.raw, best: i === bi, worst: i === wi })), gap: gapTxt }
  })

  /* chart series */
  const trendSeries = isYears ? (subjFull ? [{ name: subjFull.name, color: 'var(--sd-green-500)', v: subjFull.satYr || {} }] : []) : items.map((it) => ({ name: it.name, color: it.color, v: it.satYr || {} }))
  const trendYears = [...new Set(trendSeries.flatMap((s) => Object.keys(s.v).filter((y) => s.v[y] != null)))].sort()
  const partSeries = isYears ? (subjFull && subjFull.partYr ? [{ name: subjFull.name, color: 'var(--sd-green-500)', v: subjFull.partYr }] : []) : items.filter((it) => it.partYr).map((it) => ({ name: it.name, color: it.color, v: it.partYr }))
  const partYears = [...new Set(partSeries.flatMap((s) => Object.keys(s.v)))].sort()
  const hl = isYears ? st.i.map(Number) : null
  return { verdict, ins, rows, gap, gapC, scope, noReg: items.some((it) => it.noReg), trendSeries, trendYears, partSeries, partYears, hl, shared: sharedQuestions(items) }
}

export const subjectLabel = (D, st, key, lang) => {
  if (st.d === 'surveys') { const r = lbById(D, key); return r ? surveyName(r, lang) : '#' + key }
  if (st.d === 'sectors') return sectorName(D, key, lang)
  if (st.d === 'regions') { const R = REGIONS[+key]; return R ? regionName(R.ar, lang) : key }
  if (st.d === 'cities') return cityName(key, lang)
  return key
}
